import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { FileText, Trash2, Upload, Download } from "lucide-react";

interface ModuleMaterialsManagerProps {
  moduleId: string;
  moduleTitle: string;
}

export function ModuleMaterialsManager({ moduleId, moduleTitle }: ModuleMaterialsManagerProps) {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: materials = [], isLoading } = useQuery({
    queryKey: ["module-materials", moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_materials")
        .select("*")
        .eq("module_id", moduleId)
        .order("order_index");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (material: { id: string; file_path: string }) => {
      // Delete from storage
      await supabase.storage.from("module-materials").remove([material.file_path]);
      // Delete from table
      const { error } = await supabase.from("module_materials").delete().eq("id", material.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["module-materials", moduleId] });
      toast({ title: "Material removido!" });
    },
    onError: (error) => {
      toast({ title: "Erro ao remover", description: String(error), variant: "destructive" });
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast({ title: "Apenas arquivos PDF são aceitos", variant: "destructive" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Arquivo excede 10MB", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const filePath = `${moduleId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("module-materials")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: user } = await supabase.auth.getUser();

      const { error: insertError } = await supabase.from("module_materials").insert({
        module_id: moduleId,
        title: file.name.replace(/\.pdf$/i, ""),
        file_path: filePath,
        file_size_bytes: file.size,
        uploaded_by: user?.user?.id,
        order_index: materials.length,
      });

      if (insertError) throw insertError;

      queryClient.invalidateQueries({ queryKey: ["module-materials", moduleId] });
      toast({ title: "Material enviado com sucesso!" });
    } catch (error) {
      toast({ title: "Erro ao enviar", description: String(error), variant: "destructive" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Materiais PDF</Label>
        <div className="relative">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="absolute inset-0 opacity-0 cursor-pointer w-full"
            disabled={uploading}
          />
          <Button size="sm" variant="outline" disabled={uploading}>
            <Upload className="h-3 w-3 mr-1" />
            {uploading ? "Enviando..." : "Upload PDF"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Carregando...</p>
      ) : materials.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum material adicionado.</p>
      ) : (
        <div className="space-y-2">
          {materials.map((mat: any) => (
            <div key={mat.id} className="flex items-center justify-between gap-2 p-2 rounded border bg-muted/30">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-destructive shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{mat.title}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(mat.file_size_bytes)}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { if (confirm("Remover material?")) deleteMutation.mutate({ id: mat.id, file_path: mat.file_path }); }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
